import { useToast } from "@shopify/app-bridge-react";
import { useCallback, useEffect, useState } from "react";
import Api from "../../../helpers/api";
import {
    Button,
    Modal,
    Select,
    DropZone,
    FormLayout,
    LegacyStack,
    Text
} from "@shopify/polaris";
import ImportsList from "./ImportsList";

export default ({
    locations = [],
    loading = false,
    setLoading = () => {},
    productType = null,
    mode = null,
}) => {
    const toast = useToast();
    const [internalLoading, setInternalLoading] = useState(false);
    const [modalActive, setModalActive] = useState(false);
    const [file, setFile] = useState(null);
    const [location, setLocation] = useState('');
    const [listReload, setListReload] = useState(0);

    const onClose = () => {
        setFile(null);
        setModalActive(false);
    };

    useEffect(() => {
        loadImports();
    }, []);

    const loadImports = useCallback(() => {
        setListReload(prev => prev + 1);
    }, []);

    const importInventory = useCallback(() => {
        if (!location) {
            toast.show("You need to select Location first");
            return;
        }
        if (!file) {
            toast.show("You need to upload file");
            return;
        }
        if (!productType?.value) {
            toast.show("Product type is missing");
            return;
        }
        if (!mode) {
            toast.show("Import mode is missing");
            return;
        }

        setLoading(true);
        setInternalLoading(true);

        Api.importInventory({
            file: file,
            location_id: location,
            mode: mode,
            products_type: productType.value,
            type: 'inventory',
        })
            .then(() => {
                toast.show("Inventory Import has been started");
                setFile(null);
                setLocation('');
                loadImports();
            })
            .catch(() => {
                toast.show("Server error. Please try again");
            })
            .finally(() => {
                setLoading(false);
                setInternalLoading(false);
            });
    }, [location, file, mode, productType]);

    const handleDropZoneDrop = useCallback(
        (_dropFiles, acceptedFiles) => setFile(acceptedFiles[0]),
        [],
    );

    const fileUpload = !file && <DropZone.FileUpload />;
    const uploadedFile = file && (
        <LegacyStack distribution="center" alignment="center">
            <div>
                {file.name}{' '}
                <Text variant="bodySm" as="p">
                    {file.size} bytes
                </Text>
            </div>
        </LegacyStack>
    );

    const downloadCsvSample = useCallback(() => {
        const header = productType?.value === 'furnace'
            ? [
                'IT Enable','Sku','"Concatenate with E, then delete"',
                '"Temp Item Description Copy to M, then delete"',
                '"Temp Price Copy to I, then delete"','SIZE','nm',
                'retail_price','ds','qty_available','special_price',
            ]
            : [
                'IT Enable','opt1','Sku','SIZE','nm','retail_price','status',
                'Opt2','product Line','ds','BinLocation','product_categories_vir',
                'products_vir','searchfield1','Seachfield5','featured',
                'parent_child_type','managed inventory','qty_avail','qty_add',
                '"phantom inventory(husbandry)"','"Exports/Orders"','special_price',
                'Quantity Increment','Minimum Order Quantity','Maximum Order Quantity',
                'Quantity Break 1','Price Break 1','Quantity Break 2','Price Break 2',
                'Quantity Break 3','Price Break 3','Quantity Break 4','Price Break 4',
                'Quantity Break 5','Price Break 5','Quantity Break 6','Price Break 6',
                'Quantity Break 7','Price Break 7','Quantity Break 8','Price Break 8',
                'Quantity Break 9','Price Break 9','Quantity Break 10','Price Break 10'
            ];

        const element = document.createElement('a');
        element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(header.join(',')));
        element.setAttribute('download', 'sample.csv');
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    }, [productType]);


    if (!mode) return null;

    return (
        <Modal
            open={modalActive}
            title={`${productType.label} - Import Inventory with CSV (${mode.toUpperCase()})`}
            activator={
                <Button
                    variant="primary"
                    loading={internalLoading}
                    disabled={loading && !internalLoading}
                    onClick={() => setModalActive(true)}
                >
                    {mode === 'add' ? 'Add Inventory' : 'Replace Inventory'}
                </Button>
            }
            onClose={onClose}
            secondaryActions={[
                { content: "Close", onAction: onClose },
                { content: "CSV Sample", onAction: downloadCsvSample },
            ]}
            size="large"
        >
            <Modal.Section>
                <FormLayout>
                    <FormLayout.Group>
                        <Select
                            placeholder="-- Select Location --"
                            label="Location"
                            disabled={internalLoading}
                            value={location}
                            onChange={(val) => setLocation(val.toString())}
                            options={locations.map((location) => ({
                                label: location.name,
                                value: location.id.toString(),
                            }))}
                            requiredIndicator
                        />
                    </FormLayout.Group>

                    <DropZone allowMultiple={false} onDrop={handleDropZoneDrop} accept="text/csv">
                        {uploadedFile}
                        {fileUpload}
                    </DropZone>
                </FormLayout>

                <LegacyStack>
                    <Button
                        variant="primary"
                        disabled={loading && !internalLoading}
                        loading={internalLoading}
                        onClick={importInventory}
                    >
                        Import
                    </Button>
                </LegacyStack>
            </Modal.Section>
            <Modal.Section>
                <ImportsList type="inventory" loading={loading} listReload={listReload} products_type={productType.value} />
            </Modal.Section>
        </Modal>
    );
};
